import { Fragment, type Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import { ReplaceStep } from '@atlaskit/editor-prosemirror/transform';
import type { Step } from '@atlaskit/editor-prosemirror/transform-override';

import { combineInlineTextRuns, textNodesFromRun } from './coalesce';
import { createStepsForNode } from './create-steps-for-node';
import { diffList } from './diff-list';
import { tryReplaceAround } from './replace-around';
import { diffText } from './text-diff';
import type { DiffContext, InlineItem, TextRun } from './types';
import { filterMarks, isTextRun, nodeEqIgnoring } from './utils';

const MAX_CONTENT_REGIONS = 200;

/**
 * Diffs two block fragments (children of the same parent block) and emits PM
 * steps in old-doc coordinates. `parentPos` is the position of the parent block;
 * child positions live at `parentPos + 1 + sum(prevSiblingNodeSizes)`.
 *
 * Mirrors `DiffUtils.diffContent`.
 */
export function diffBlockContent(
	oldContent: Fragment,
	newContent: Fragment,
	parentPos: number,
	ctx: DiffContext,
): Step[] {
	const oldKids: PMNode[] = [];
	for (let i = 0; i < oldContent.childCount; i++) oldKids.push(oldContent.child(i));
	const newKids: PMNode[] = [];
	for (let i = 0; i < newContent.childCount; i++) newKids.push(newContent.child(i));

	const positions = childPositions(parentPos, oldKids);
	const steps: Step[] = [];

	diffList<PMNode>(
		oldKids,
		newKids,
		(op) => {
			switch (op.kind) {
				case 'match': {
					let childPos = positions[op.oldIndex];
					for (let i = 0; i < op.length; i++) {
						steps.push(
							...createStepsForNode(
								oldKids[op.oldIndex + i],
								newKids[op.newIndex + i],
								childPos,
								ctx,
							),
						);
						childPos += oldKids[op.oldIndex + i].nodeSize;
					}
					return;
				}
				case 'insert': {
					const insertPos = positions[op.insertAt];
					const slice = blockSlice(newKids.slice(op.from, op.to));
					// structure=true on Insert is safe (the range is empty) and gives
					// better rebase semantics: a concurrent insert at the same position
					// won't be silently clobbered.
					steps.push(new ReplaceStep(insertPos, insertPos, slice, true));
					return;
				}
				case 'delete': {
					steps.push(new ReplaceStep(positions[op.from], positions[op.to], Slice.empty));
					return;
				}
				case 'replace': {
					// Single-node-to-single-node Replace ops are the only shape where a
					// structural ReplaceAround can apply (type change preserving content,
					// wrap, or unwrap). Try that first; fall back to a wholesale
					// ReplaceStep for anything more complex.
					if (op.replaceTo - op.replaceFrom === 1 && op.to - op.from === 1) {
						const around = tryReplaceAround(
							oldKids[op.replaceFrom],
							newKids[op.from],
							positions[op.replaceFrom],
							ctx,
						);
						if (around) {
							steps.push(around);
							return;
						}
					}
					const slice = blockSlice(newKids.slice(op.from, op.to));
					steps.push(new ReplaceStep(positions[op.replaceFrom], positions[op.replaceTo], slice));
					return;
				}
			}
		},
		nodesMatchable,
		MAX_CONTENT_REGIONS,
		(a, b) => nodeEqIgnoring(a, b, ctx.ignoreMarks),
	);

	return steps;
}

/**
 * Diffs two fragments of INLINE content (paragraph/heading children). Coalesces
 * consecutive text nodes into TextRuns first so mark-aware text diffing works.
 *
 * `parentPos` is the position of the parent block.
 */
export function diffInlineContent(
	oldContent: Fragment,
	newContent: Fragment,
	parentPos: number,
	ctx: DiffContext,
): Step[] {
	const oldItems = combineInlineTextRuns(oldContent);
	const newItems = combineInlineTextRuns(newContent);

	const positions = inlineChildPositions(parentPos, oldItems);
	const steps: Step[] = [];

	diffList<InlineItem>(
		oldItems,
		newItems,
		(op) => {
			switch (op.kind) {
				case 'match': {
					let childPos = positions[op.oldIndex];
					for (let i = 0; i < op.length; i++) {
						const a = oldItems[op.oldIndex + i];
						const b = newItems[op.newIndex + i];
						if (isTextRun(a) && isTextRun(b)) {
							steps.push(...diffTextRunPair(a, b, childPos, ctx));
							childPos += a.text.length;
						} else if (!isTextRun(a) && !isTextRun(b)) {
							steps.push(...createStepsForNode(a.node, b.node, childPos, ctx));
							childPos += a.node.nodeSize;
						} else {
							// Shouldn't happen if `nodesMatchable` worked correctly.
							throw new Error('inline match between textRun and non-text node');
						}
					}
					return;
				}
				case 'insert': {
					const insertPos = positions[op.insertAt];
					const nodes = inlineNodesFromItems(newItems.slice(op.from, op.to), ctx);
					steps.push(new ReplaceStep(insertPos, insertPos, new Slice(Fragment.from(nodes), 0, 0)));
					return;
				}
				case 'delete': {
					steps.push(new ReplaceStep(positions[op.from], positions[op.to], Slice.empty));
					return;
				}
				case 'replace': {
					const nodes = inlineNodesFromItems(newItems.slice(op.from, op.to), ctx);
					steps.push(
						new ReplaceStep(
							positions[op.replaceFrom],
							positions[op.replaceTo],
							new Slice(Fragment.from(nodes), 0, 0),
						),
					);
					return;
				}
			}
		},
		inlineItemsMatchable,
		MAX_CONTENT_REGIONS,
		(a, b) => inlineItemsStrictEq(a, b, ctx.ignoreMarks),
	);

	return steps;
}

/**
 * Strict equality for inline items, ignoring marks of types in `ignore`. Used
 * by the inline prefix/suffix strip so a TextRun that differs only in ignored
 * marks (e.g. an annotation present in pmBase but absent in pmNew) gets
 * stripped cheaply instead of going through the LCS path.
 */
function inlineItemsStrictEq(a: InlineItem, b: InlineItem, ignore: ReadonlySet<string>): boolean {
	if (isTextRun(a) && isTextRun(b)) {
		if (a.text !== b.text) return false;
		// Per-character mark equality on the filtered mark sets. Span boundaries
		// can differ between sides without that being a real difference, so we
		// compare position-by-position rather than span-by-span.
		let ai = 0;
		let bi = 0;
		for (let p = 0; p < a.text.length; p++) {
			while (ai < a.spans.length && a.spans[ai].end <= p) ai++;
			while (bi < b.spans.length && b.spans[bi].end <= p) bi++;
			const aMarks = ai < a.spans.length ? filterMarks(a.spans[ai].marks, ignore) : [];
			const bMarks = bi < b.spans.length ? filterMarks(b.spans[bi].marks, ignore) : [];
			if (aMarks.length !== bMarks.length) return false;
			if (!aMarks.every((m) => bMarks.some((n) => n.eq(m)))) return false;
		}
		return true;
	}
	if (!isTextRun(a) && !isTextRun(b)) return a.node.eq(b.node);
	return false;
}

function diffTextRunPair(a: TextRun, b: TextRun, pos: number, ctx: DiffContext): Step[] {
	return diffText(a, b, pos, ctx);
}

function inlineNodesFromItems(items: InlineItem[], ctx: DiffContext): PMNode[] {
	const schema = ctx.schema;
	const out: PMNode[] = [];
	for (const it of items) {
		if (isTextRun(it)) {
			out.push(...textNodesFromRun(it, 0, it.text.length, schema.text.bind(schema)));
		} else {
			out.push(it.node);
		}
	}
	return out;
}

function blockSlice(nodes: readonly PMNode[]): Slice {
	return new Slice(Fragment.from(nodes), 0, 0);
}

/**
 * "Similar enough to be a match candidate" for the block-level LCS:
 * - both nodes share the same type (attrs may differ; handled in recursion)
 * - text equality lives inside the inline path, not here
 */
function nodesMatchable(a: PMNode, b: PMNode): boolean {
	return a.type === b.type;
}

function inlineItemsMatchable(a: InlineItem, b: InlineItem): boolean {
	if (isTextRun(a) && isTextRun(b)) return true;
	if (!isTextRun(a) && !isTextRun(b)) return a.node.type === b.node.type;
	return false;
}

function childPositions(parentPos: number, kids: readonly PMNode[]): number[] {
	const out = new Array<number>(kids.length + 1);
	out[0] = parentPos + 1;
	for (let i = 0; i < kids.length; i++) out[i + 1] = out[i] + kids[i].nodeSize;
	return out;
}

function inlineChildPositions(parentPos: number, items: readonly InlineItem[]): number[] {
	const out = new Array<number>(items.length + 1);
	out[0] = parentPos + 1;
	for (let i = 0; i < items.length; i++) {
		const size = isTextRun(items[i])
			? (items[i] as TextRun).text.length
			: (items[i] as { node: PMNode }).node.nodeSize;
		out[i + 1] = out[i] + size;
	}
	return out;
}
